import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  BtnPrimary, BtnSecondary, BtnGhost, Modal, Field, Input, Empty,
} from "@/components/primitives";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Tag } from "@phosphor-icons/react";

const LABEL = {
  cost_center: "Cost Centers",
  cost_category: "Cost Categories",
  department: "Departments",
  service_category: "Service Categories",
};

const HINTS = {
  cost_center: "Assigned on POs, requests, fuel logs, and weekly budget lines.",
  cost_category: "Budget-line classification. Appears on POs, requests, fuel and maintenance.",
  department: "Employee department + org-chart grouping. Numeric code = external ledger dept.",
  service_category: "Maintenance service types (e.g. 250Hr Service). Meta.service_hours drives the recurrence interval.",
};

export default function LookupsPanel({ kind }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", code: "", order: 0, active: true });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/lookups/${kind}?include_inactive=true`);
      setRows(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [kind]);

  function openNew() { setEdit(null); setForm({ name: "", code: "", order: rows.length + 1, active: true }); setOpen(true); }
  function openEdit(r) {
    setEdit(r);
    setForm({ name: r.name, code: r.code || "", order: r.order || 0, active: r.active !== false, meta: r.meta || {} });
    setOpen(true);
  }

  async function save() {
    if (!form.name) return toast.error("Name is required");
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code?.trim() || null,
        order: Number(form.order || 0),
        active: form.active !== false,
        meta: form.meta || {},
      };
      if (edit) await api.put(`/lookups/${kind}/${edit.id}`, payload);
      else await api.post(`/lookups/${kind}`, payload);
      toast.success(edit ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(formatError(e)); }
  }

  async function remove(r) {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try {
      await api.delete(`/lookups/${kind}/${r.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatError(e)); }
  }

  return (
    <div data-testid={`lookups-panel-${kind}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-[#52525b] flex-1 pr-4">{HINTS[kind]}</div>
        <BtnPrimary onClick={openNew} data-testid={`lookup-${kind}-new`}>
          <Plus size={12} weight="bold" className="inline mr-1.5" /> New
        </BtnPrimary>
      </div>

      {loading ? (
        <div className="phizo-label">Loading…</div>
      ) : rows.length === 0 ? (
        <Empty label={`No ${LABEL[kind].toLowerCase()} yet.`} />
      ) : (
        <div className="border border-[#e4e4e7]">
          <div className="overflow-x-auto">
            <table className="phizo-table">
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>#</th>
                  <th>Name</th>
                  {kind === "department" && <th>Dept Code</th>}
                  {kind === "service_category" && <th className="text-right">Interval (hrs)</th>}
                  <th className="text-center">Active</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`lookup-${kind}-row-${r.id}`}>
                    <td className="mono text-xs text-[#a1a1aa]">{r.order}</td>
                    <td className="font-medium flex items-center gap-2">
                      <Tag size={12} weight="bold" className="text-[#002FA7]" /> {r.name}
                    </td>
                    {kind === "department" && <td className="mono text-xs">{r.code || "—"}</td>}
                    {kind === "service_category" && (
                      <td className="text-right mono">{r.meta?.service_hours ?? "—"}</td>
                    )}
                    <td className="text-center">
                      {r.active !== false ? (
                        <span className="phizo-status" style={{ background: "#00C853", color: "#09090b", borderColor: "#00C853" }}>Active</span>
                      ) : (
                        <span className="phizo-status" style={{ background: "#a1a1aa", color: "#fff", borderColor: "#a1a1aa" }}>Archived</span>
                      )}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <BtnGhost onClick={() => openEdit(r)} data-testid={`lookup-${kind}-edit-${r.id}`}>
                        <PencilSimple size={12} weight="bold" />
                      </BtnGhost>
                      <BtnGhost onClick={() => remove(r)} className="text-[#FF2A2A]"
                        data-testid={`lookup-${kind}-delete-${r.id}`}>
                        <Trash size={12} weight="bold" />
                      </BtnGhost>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)}
        title={edit ? `Edit ${LABEL[kind].slice(0, -1)}` : `New ${LABEL[kind].slice(0, -1)}`}
        testId={`lookup-${kind}-modal`}>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name *">
            <Input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid={`lookup-${kind}-name`} />
          </Field>
          <Field label="Sort Order">
            <Input type="number" value={form.order}
              onChange={(e) => setForm({ ...form, order: e.target.value })} />
          </Field>
          {kind === "department" && (
            <Field label="Department Code">
              <Input value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="e.g. 1589" />
            </Field>
          )}
          {kind === "service_category" && (
            <Field label="Service Interval (hours)"
              hint="Machine service is triggered every N running hours.">
              <Input type="number" step="1"
                value={form.meta?.service_hours || ""}
                onChange={(e) => setForm({ ...form, meta: { ...form.meta, service_hours: Number(e.target.value) } })} />
            </Field>
          )}
          <Field label="Active">
            <select
              className="w-full border border-[#e4e4e7] p-2 text-sm bg-white dark:bg-[#17171c]"
              value={form.active ? "yes" : "no"}
              onChange={(e) => setForm({ ...form, active: e.target.value === "yes" })}>
              <option value="yes">Active</option>
              <option value="no">Archived</option>
            </select>
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-4 border-t border-[#e4e4e7] mt-4">
          <BtnSecondary onClick={() => setOpen(false)}>Cancel</BtnSecondary>
          <BtnPrimary onClick={save} data-testid={`lookup-${kind}-save`}>
            {edit ? "Save Changes" : "Create"}
          </BtnPrimary>
        </div>
      </Modal>
    </div>
  );
}
